import React, { useEffect, useState } from 'react'

import { useFetchAndLoad } from 'hooks'
import { useNavigate } from 'react-router-dom'
import {
  getNotifications,
  markMultipleNotificationsAsRead,
  markNotificationAsRead,
} from 'services'

export interface Notification {
  uuid: string
  title: string
  content?: string
  description: string
  created_at: string
  is_read: boolean
  action?: string | null
  status: string
  type: string
  channel: string
  updated_at: string
  image_url: string | null
  metadata?: Record<string, any>
}

export interface NotificationsInputProps {
  tooltip: string
}

export interface NotificationsProps {
  handleClick: (event: React.MouseEvent<HTMLElement>) => void
  open: boolean
  anchorEl: HTMLElement | null
  handleClose: () => void
  navigate: any
  notifications: Notification[]
  unreadCount: number
  loading: boolean
  handleNotificationClick: (notification: Notification) => void
  markAllAsRead: () => void
  tooltip: string
  hasMore: boolean
  loadMore: () => void
  loadingMore: boolean
  popupModalOpen: boolean
  currentPopupNotification: Notification | null
  handlePopupModalClose: () => void
}

export default function useNotifications({ tooltip }: NotificationsInputProps) {
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const { callEndpoint } = useFetchAndLoad()

  // State for popup notifications
  const [popupModalOpen, setPopupModalOpen] = useState(false)
  const [currentPopupNotification, setCurrentPopupNotification] =
    useState<Notification | null>(null)

  const fetchNotifications = async (page = 1, append = false) => {
    if (page === 1) {
      setLoading(true)
    } else {
      setLoadingMore(true)
    }

    try {
      const response = await callEndpoint(
        getNotifications(false, page, 10, true, 'SILENT')
      )
      const newNotifications = response.data.results

      if (append) {
        setNotifications(prev => [...prev, ...newNotifications])
      } else {
        setNotifications(newNotifications)
      }

      // Check if there are more pages
      setHasMore(!!response.data.next)

      // Update unread count
      const unreadNotifications = response.data.results.filter(
        (notification: Notification) => notification.status !== 'READ'
      )

      if (!append) {
        setUnreadCount(unreadNotifications.length)
      }

      return response.data
    } catch (error) {
      console.error('Error fetching notifications:', error)
      return null
    } finally {
      if (page === 1) {
        setLoading(false)
      } else {
        setLoadingMore(false)
      }
    }
  }

  // Fetch popup notifications
  const fetchPopupNotifications = async () => {
    try {
      const response = await callEndpoint(
        getNotifications(false, 1, 1, true, 'POPUP')
      )

      const popupNotifications = response.data.results

      // If there are unread popup notifications, show the first one
      const unreadPopupNotifications = popupNotifications.filter(
        (notification: Notification) => notification.status !== 'READ'
      )

      if (unreadPopupNotifications.length > 0) {
        setCurrentPopupNotification(unreadPopupNotifications[0])
        setPopupModalOpen(true)
      }

      return response.data
    } catch (error) {
      console.error('Error fetching popup notifications:', error)
      return null
    }
  }

  const loadMore = async () => {
    if (loadingMore || !hasMore) return

    const nextPage = currentPage + 1
    const result = await fetchNotifications(nextPage, true)

    if (result) {
      setCurrentPage(nextPage)
    }
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  // Handle popup modal close
  const handlePopupModalClose = async () => {
    if (currentPopupNotification) {
      try {
        // Mark notification as read
        await callEndpoint(
          markNotificationAsRead(currentPopupNotification.uuid)
        )

        // Update local state to reflect the change immediately
        setNotifications(prevNotifications =>
          prevNotifications.map(n =>
            n.uuid === currentPopupNotification.uuid
              ? { ...n, status: 'READ', is_read: true }
              : n
          )
        )

        // Update unread count if this was a notification in our list
        const notificationInList = notifications.some(
          n => n.uuid === currentPopupNotification.uuid
        )
        if (notificationInList) {
          setUnreadCount(prevCount => Math.max(0, prevCount - 1))
        }
      } catch (error) {
        console.error('Error marking popup notification as read:', error)
      }
    }

    setPopupModalOpen(false)
    setCurrentPopupNotification(null)
  }

  const extractPath = (url: string): string => {
    // Si ya viene como path (empieza con /), usarlo directamente
    if (url.startsWith('/')) {
      return url
    }

    // Si viene como URL completa, extraer solo el path
    try {
      const urlObj = new URL(url)
      return urlObj.pathname
    } catch {
      // Si no se puede parsear como URL, asumir que es un path y devolverlo
      return url.startsWith('/') ? url : `/${url}`
    }
  }

  const handleNotificationClick = async (notification: Notification) => {
    try {
      // Mark notification as read
      if (notification.status !== 'READ') {
        await callEndpoint(markNotificationAsRead(notification.uuid))
        // Update local state to reflect the change
        setNotifications(prevNotifications =>
          prevNotifications.map(n =>
            n.uuid === notification.uuid
              ? { ...n, status: 'READ', is_read: true }
              : n
          )
        )
        // Update unread count
        setUnreadCount(prevCount => Math.max(0, prevCount - 1))
      }

      // Navigate if there's a valid action
      if (notification.action) {
        const path = extractPath(notification.action)
        navigate(path)
      }

      // Close the menu
      handleClose()
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const markAllAsRead = async () => {
    const unreadIds = notifications
      .filter(notification => notification.status !== 'READ')
      .map(notification => notification.uuid)

    if (unreadIds.length === 0) return

    try {
      await markMultipleNotificationsAsRead(unreadIds)

      // Update local state
      setNotifications(prevNotifications =>
        prevNotifications.map(notification => ({
          ...notification,
          status: 'READ',
          is_read: true,
        }))
      )
      setUnreadCount(0)

      // Close the menu
      handleClose()
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    }
  }

  // Fetch notifications on component mount and every 5 minutes
  useEffect(() => {
    fetchNotifications()
    fetchPopupNotifications() // Fetch popup notifications initially

    const intervalId = setInterval(() => {
      fetchNotifications()
      fetchPopupNotifications() // Also check for new popup notifications periodically
    }, 5 * 60 * 1000)

    return () => clearInterval(intervalId)
  }, [])

  return {
    handleClick,
    open,
    anchorEl,
    handleClose,
    navigate,
    notifications,
    unreadCount,
    loading,
    handleNotificationClick,
    markAllAsRead,
    tooltip,
    hasMore,
    loadMore,
    loadingMore,
    popupModalOpen,
    currentPopupNotification,
    handlePopupModalClose,
  }
}
